import React from 'react';

type FlexDirection = 'row' | 'col' | 'row-reverse' | 'col-reverse';
type FlexJustify = 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
type FlexAlign = 'start' | 'end' | 'center' | 'stretch' | 'baseline';
type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

type FlexElement = 'div' | 'header' | 'nav' | 'main' | 'section' | 'article' | 'aside' | 'footer' | 'span';

export interface FlexProps extends Omit<React.HTMLAttributes<HTMLElement>, 'as' | 'className'> {
  direction?: FlexDirection;
  responsiveDirection?: FlexDirection; // For responsive breakpoints (e.g., md:flex-row)
  justify?: FlexJustify;
  align?: FlexAlign;
  wrap?: FlexWrap;
  gap?: number | string;
  as?: FlexElement;
  grow?: boolean;
  mb?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  mt?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  mr?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  ml?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  py?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  px?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  fixed?: boolean;
  inset?: boolean;
  zIndex?: number;
  bg?: string;
  backdrop?: boolean | 'md';
  animate?: 'fadeIn' | 'none';
  minHeight?: 'screen' | 'full' | 'auto';
  font?: 'sans' | 'serif' | 'mono';
  textColor?: 'white' | 'black' | 'muted';
  border?: 'b' | 't' | 'l' | 'r' | 'x' | 'y' | 'all' | 'none';
  borderColor?: 'white/10' | 'white/20' | 'default';
  sticky?: boolean;
  top?: 0 | 'auto';
  children: React.ReactNode;
}

const directionMap: Record<FlexDirection, string> = {
  row: 'flex-row',
  col: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'col-reverse': 'flex-col-reverse',
};

const justifyMap: Record<FlexJustify, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

const alignMap: Record<FlexAlign, string> = {
  start: 'items-start',
  end: 'items-end',
  center: 'items-center',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const wrapMap: Record<FlexWrap, string> = {
  nowrap: 'flex-nowrap',
  wrap: 'flex-wrap',
  'wrap-reverse': 'flex-wrap-reverse',
};

const mbMap: Record<number, string> = {
  0: 'mb-0',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  5: 'mb-5',
  6: 'mb-6',
  8: 'mb-8',
  10: 'mb-10',
  12: 'mb-12',
};

const mtMap: Record<number, string> = {
  0: 'mt-0',
  1: 'mt-1',
  2: 'mt-2',
  3: 'mt-3',
  4: 'mt-4',
  5: 'mt-5',
  6: 'mt-6',
  8: 'mt-8',
  10: 'mt-10',
  12: 'mt-12',
};

const mrMap: Record<number, string> = {
  0: 'mr-0',
  1: 'mr-1',
  2: 'mr-2',
  3: 'mr-3',
  4: 'mr-4',
  5: 'mr-5',
  6: 'mr-6',
  8: 'mr-8',
  10: 'mr-10',
  12: 'mr-12',
};

const mlMap: Record<number, string> = {
  0: 'ml-0',
  1: 'ml-1',
  2: 'ml-2',
  3: 'ml-3',
  4: 'ml-4',
  5: 'ml-5',
  6: 'ml-6',
  8: 'ml-8',
  10: 'ml-10',
  12: 'ml-12',
};

const pyMap: Record<number, string> = {
  0: 'py-0',
  1: 'py-1',
  2: 'py-2',
  3: 'py-3',
  4: 'py-4',
  5: 'py-5',
  6: 'py-6',
  8: 'py-8',
  10: 'py-10',
  12: 'py-12',
};

const pxMap: Record<number, string> = {
  0: 'px-0',
  1: 'px-1',
  2: 'px-2',
  3: 'px-3',
  4: 'px-4',
  5: 'px-5',
  6: 'px-6',
  8: 'px-8',
  10: 'px-10',
  12: 'px-12',
};

const animateMap: Record<string, string> = {
  fadeIn: 'animate-[fadeIn_0.2s_ease]',
  none: '',
};

export const Flex: React.FC<FlexProps> = ({
  direction = 'row',
  responsiveDirection,
  justify,
  align,
  wrap,
  gap,
  as = 'div',
  grow,
  mb,
  mt,
  mr,
  ml,
  py,
  px,
  fixed,
  inset,
  zIndex,
  bg,
  backdrop,
  animate,
  minHeight,
  font,
  textColor,
  border,
  borderColor,
  sticky,
  top,
  children,
  style,
  ...props
}) => {
  const gapClass =
    gap !== undefined && typeof gap === 'number'
      ? `gap-${gap}`
      : gap !== undefined && typeof gap === 'string' && /^\d+$/.test(gap)
      ? `gap-${gap}`
      : '';

  const responsiveClass = responsiveDirection
    ? `md:${directionMap[responsiveDirection]}`
    : '';

  const bgClass = bg === 'black/80' ? 'bg-black/80' : bg === '#0a0a0a' ? 'bg-[#0a0a0a]' : bg === '#0a0a0a/80' ? 'bg-[#0a0a0a]/80' : bg ? `bg-${bg}` : '';
  const minHeightClass = minHeight === 'screen' ? 'min-h-screen' : minHeight === 'full' ? 'min-h-full' : '';
  const fontClass = font === 'sans' ? 'font-sans' : font === 'serif' ? 'font-serif' : font === 'mono' ? 'font-mono' : '';
  const textColorClass = textColor === 'white' ? 'text-white' : textColor === 'black' ? 'text-black' : textColor === 'muted' ? 'text-white/50' : '';
  const borderClass = border === 'b' ? 'border-b' : border === 't' ? 'border-t' : border === 'l' ? 'border-l' : border === 'r' ? 'border-r' : border === 'x' ? 'border-x' : border === 'y' ? 'border-y' : border === 'all' ? 'border' : '';
  const borderColorClass = borderColor === 'white/10' ? 'border-white/10' : borderColor === 'white/20' ? 'border-white/20' : '';
  const stickyClass = sticky ? 'sticky' : '';
  const topClass = top === 0 ? 'top-0' : top === 'auto' ? 'top-auto' : '';
  const backdropClass = backdrop === true ? 'backdrop-blur-sm' : backdrop === 'md' ? 'backdrop-blur-md' : '';

  const classes = [
    'flex',
    directionMap[direction],
    justify && justifyMap[justify],
    align && alignMap[align],
    wrap && wrapMap[wrap],
    gapClass,
    grow && 'flex-1',
    responsiveClass,
    mb !== undefined && mbMap[mb],
    mt !== undefined && mtMap[mt],
    mr !== undefined && mrMap[mr],
    ml !== undefined && mlMap[ml],
    py !== undefined && pyMap[py],
    px !== undefined && pxMap[px],
    fixed && 'fixed',
    inset && 'inset-0',
    zIndex !== undefined && `z-[${zIndex}]`,
    bgClass,
    backdropClass,
    animate && animateMap[animate],
    minHeightClass,
    fontClass,
    textColorClass,
    borderClass,
    borderColorClass,
    stickyClass,
    topClass,
  ]
    .filter(Boolean)
    .join(' ');

  const inlineStyle = {
    ...style,
    ...(gap !== undefined &&
      typeof gap === 'string' &&
      !gap.match(/^\d+$/) && { gap }),
  };

  const Component = as as React.ElementType;
  
  return (
    <Component className={classes} style={inlineStyle} {...props}>
      {children}
    </Component>
  );
};

